import React, { memo, useId } from 'react';
import { iconUrl } from '../utils/assets';
import Glyph, { glyphName } from './Glyph';
import IconImg from './IconImg';

/** Animated Telegram Stars — CSS-only, GPU-friendly. */
export const StarIcon = memo(function StarIcon({ size = 18, className = '' }) {
  const uid = useId().replace(/:/g, '');
  const grad = `starGrad-${uid}`;
  const glow = `starGlow-${uid}`;

  return (
    <span className={`fx-icon fx-star ${className}`} style={{ width: size, height: size }} aria-hidden>
      <svg viewBox="0 0 32 32" className="fx-icon-svg">
        <defs>
          <linearGradient id={grad} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fff6c2" />
            <stop offset="45%" stopColor="#ffd166" />
            <stop offset="100%" stopColor="#f4a261" />
          </linearGradient>
          <filter id={glow} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="1.2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <g className="fx-star-body" filter={`url(#${glow})`}>
          <path
            fill={`url(#${grad})`}
            d="M16 2.2l3.6 8.2 8.9.8-6.7 5.9 2.1 8.7L16 21.4 7.9 25.8l2.1-8.7L3.3 11.2l8.9-.8L16 2.2z"
          />
        </g>
        <circle className="fx-star-spark a" cx="8" cy="7" r="1.1" fill="#fff" />
        <circle className="fx-star-spark b" cx="25" cy="12" r="0.9" fill="#fff" />
        <circle className="fx-star-spark c" cx="12" cy="26" r="0.8" fill="#fff" />
      </svg>
    </span>
  );
});

/**
 * The premium currency crystal — a cut gem, not a flat polygon.
 *
 * The whole Atelier economy hangs off this icon, so it has to read as *treasure*
 * at 14px and hold up at 64px: a jewel-cut silhouette, individually shaded
 * facets (crown / table / pavilion), an inner fire glow, a sweeping specular
 * highlight and two twinkles. Everything is pure SVG + CSS — no assets, no
 * per-frame JS, and the motion is opt-out via `still` for dense lists.
 */
export const RubyIcon = memo(function RubyIcon({ size = 18, className = '', still = false }) {
  const uid = useId().replace(/:/g, '');
  const body = `gemBody-${uid}`;
  const crown = `gemCrown-${uid}`;
  const fire = `gemFire-${uid}`;
  const sweep = `gemSweep-${uid}`;
  const clip = `gemClip-${uid}`;
  const glow = `gemGlow-${uid}`;
  // Classic brilliant silhouette: wide crown, girdle at 11.6, pavilion
  // converging to a point. Reused by the body, the stroke and the sweep clip.
  const CUT = 'M16 3L28.5 11.6L16 29.4L3.5 11.6L16 3z';

  return (
    <span
      className={`fx-icon fx-ruby ${still ? 'is-still' : ''} ${className}`}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <svg viewBox="0 0 32 32" className="fx-icon-svg">
        <defs>
          <linearGradient id={body} x1="14%" y1="0%" x2="82%" y2="100%">
            <stop offset="0%" stopColor="#ffd9e2" />
            <stop offset="26%" stopColor="#ff8fab" />
            <stop offset="62%" stopColor="#ff2d55" />
            <stop offset="100%" stopColor="#8d0b31" />
          </linearGradient>
          <linearGradient id={crown} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.9" />
            <stop offset="70%" stopColor="#fff" stopOpacity="0.08" />
          </linearGradient>
          <radialGradient id={fire} cx="50%" cy="58%" r="55%">
            <stop offset="0%" stopColor="#fff3f6" stopOpacity="0.85" />
            <stop offset="55%" stopColor="#ff5c7c" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#ff1744" stopOpacity="0" />
          </radialGradient>
          <linearGradient id={sweep} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#fff" stopOpacity="0" />
            <stop offset="50%" stopColor="#fff" stopOpacity="0.75" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
          <filter id={glow} x="-45%" y="-45%" width="190%" height="190%">
            {/* Мягкий, но не «размывающий» ореол: на 14px огранка обязана
                остаться читаемой, иначе камень превращается в пятно. */}
            <feGaussianBlur stdDeviation="0.55" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <clipPath id={clip}>
            <path d={CUT} />
          </clipPath>
        </defs>

        <g className="fx-ruby-body" filter={`url(#${glow})`}>
          <path fill={`url(#${body})`} d={CUT} />

          {/* pavilion — three facets converging on the culet, each lit
              differently so the stone has depth instead of a flat fill */}
          <path fill="rgba(40,0,14,0.52)" d="M3.5 11.6L16 29.4L10.6 11.6z" />
          <path fill="rgba(255,255,255,0.10)" d="M10.6 11.6L16 29.4L21.4 11.6z" />
          <path fill="rgba(40,0,14,0.3)" d="M21.4 11.6L16 29.4L28.5 11.6z" />

          {/* crown facets — table plus the two shoulder cuts */}
          <path fill={`url(#${crown})`} d="M16 3L21.4 11.6H10.6L16 3z" opacity="0.8" />
          <path fill="rgba(255,255,255,0.32)" d="M16 3L28.5 11.6h-7.1L16 3z" />
          <path fill="rgba(255,255,255,0.15)" d="M16 3L3.5 11.6h7.1L16 3z" />

          {/* inner fire — the stone looks lit from inside, not painted */}
          <path className="fx-ruby-fire" fill={`url(#${fire})`} d={CUT} />

          {/* girdle + facet edges */}
          <path fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="0.7" d={CUT} />
          <path
            fill="none"
            stroke="rgba(255,255,255,0.28)"
            strokeWidth="0.55"
            d="M3.5 11.6h25M10.6 11.6L16 29.4l5.4-17.8M10.6 11.6L16 3l5.4 8.6"
          />

          {/* specular sweep, clipped to the cut so it never bleeds outside */}
          <g clipPath={`url(#${clip})`}>
            <rect className="fx-ruby-sweep" x="-16" y="-4" width="12" height="40" fill={`url(#${sweep})`} />
          </g>
        </g>

        <circle className="fx-ruby-spark a" cx="23.6" cy="8.4" r="1.15" fill="#fff" />
        <circle className="fx-ruby-spark b" cx="8.6" cy="16.4" r="0.85" fill="#fff" />
      </svg>
    </span>
  );
});

/**
 * Telegram animated emoji (GIF) / static PNG wrapper.
 * Native GIF motion is the animation — no CSS transform on GIFs (avoids jank).
 */
export const FloatIcon = memo(function FloatIcon({
  src,
  alt = '',
  size = 28,
  className = '',
  animated = true,
  eager = false,
  native = true,
  vector = false,
}) {
  // Интерфейсные эмодзи-PNG заменены векторным набором: одна отрисовка на всех
  // платформах, единая палитра и оптический вес. Игровой арт (upg_/inv_/cos_)
  // сюда не попадает и по-прежнему грузится картинкой.
  //
  // По умолчанию рисуем настоящий файл: в /assets/icons лежат анимированные
  // GIF-стикеры Telegram (96×96) с расширением .png, и подменять их плоским
  // вектором нельзя — интерфейс сразу читается как чужой. Векторный набор
  // остаётся доступен точечно, через явный проп `vector`.
  const shape = vector ? glyphName(src) : null;
  if (shape) {
    return <Glyph name={shape} size={size} className={className} title={alt} />;
  }

  const isGif = typeof src === 'string' && /\.gif($|\?)/i.test(src);
  // Our Telegram stickers are shipped as *.png but are GIFs — treat menu/title icons as native.
  // Stickers shipped as *.png are often GIF — always treat native flag / sniff
  const lookNative = native || isGif || (typeof src === 'string' && /icons\/(trophy|bulb|question|muscle|gem|cross|fire|glowing_star|rocket|crown|handshake|chart_up|toolbox|home|folder|energy_new|button|coin)\.png/i.test(src));
  const live = animated && !lookNative;

  return (
    <span
      className={`fx-icon-wrap ${animated ? 'is-on' : 'is-off'} ${className}`}
      style={{ width: size, height: size }}
      aria-hidden={alt ? undefined : true}
    >
      <IconImg
        src={src}
        alt={alt}
        // Стикеры всегда живые. Была попытка отдавать неактивным вкладкам
        // застывший первый кадр — формально этого и просил проп `animated`,
        // но на экране это читается как пропавшая анимация. Не делаем так.
        width={size}
        height={size}
        className={`fx-float-img ${lookNative ? 'is-native' : ''} ${live ? 'is-alive' : ''}`}
        decoding="async"
        loading={eager ? 'eager' : 'lazy'}
        fetchPriority={eager ? 'high' : 'auto'}
        draggable={false}
      />
    </span>
  );
});

export const CoinIcon = memo(function CoinIcon({ size = 18, className = '' }) {
  return (
    <FloatIcon
      src={iconUrl('coin.png')}
      size={size}
      className={`fx-coin ${className}`}
      // is-on so inactive-native dimming does not grey out the coin;
      // native keeps it static (no bob / 3D tilt).
      animated
      native
      eager
    />
  );
});

/** Shared screen title with Telegram emoji art. */
export const ScreenTitle = memo(function ScreenTitle({
  icon,
  title,
  subtitle,
  children,
  size = 72,
}) {
  return (
    <div className="screen-title">
      {icon ? (
        <FloatIcon src={icon} size={size} className="title-img" eager animated native />
      ) : null}
      <h1 className="title-span">{title}</h1>
      {subtitle ? <p className="title-sub">{subtitle}</p> : null}
      {children}
    </div>
  );
});
